import React from 'react';
import { Service } from '../types';

interface ServiceSectionProps {
  service: Service;
  index: number;
  showPackages?: boolean;
  pricingFirst?: boolean;
  beforePackages?: React.ReactNode;
  id?: string;
  backgroundImage?: boolean;
  showTitleBlock?: boolean;
}

const ServiceSection: React.FC<ServiceSectionProps> = ({ service, index, showPackages = true, pricingFirst = false, beforePackages, id, backgroundImage = true, showTitleBlock = true }) => {
  // Special background for Internet, Intercom, Surveillance, Telephony, and TV services
  const isInternetService = service.title === "Internet";
  const isIntercomService = service.title === "Intercom";
  const isSurveillanceService = service.title === "Supraveghere Video";
  const isTelephonyService = service.title === "Telefonie";
  const isTVService = service.title === "Televiziune";
  const hasSpecialBackground = (isInternetService || isIntercomService || isSurveillanceService || isTelephonyService || isTVService) && backgroundImage;

  const sectionStyle = {
    backgroundImage: isInternetService
      ? 'url("/img/hero-internet.jpg")'
      : isIntercomService
        ? 'url("/img/hero-intercom.jpg")'
        : isSurveillanceService
          ? 'url("/img/hero-supraveghere.jpg")'
          : isTelephonyService
            ? 'url("/img/hero-telefonie.jpg")'
            : isTVService
              ? 'url("/img/hero-tv.jpg")'
              : undefined,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    position: 'relative' as const,
  };

  const descriptionBlock = (
    <div className="text-center mb-12 max-w-4xl mx-auto">
      <div className={`backdrop-blur-sm p-8 rounded-2xl ${hasSpecialBackground ? 'bg-transparent' : 'bg-white/80'}`}>
        <p className={`text-xl mb-8 ${hasSpecialBackground ? 'text-white' : 'text-primary'}`}>
          {service.description}
        </p>
        <ul className="text-lg space-y-4 text-left">
          {service.features?.map((feature, i) => (
            <li key={i} className={`flex items-center ${hasSpecialBackground ? 'text-white' : 'text-gray-700'}`}>
              <span className="mr-3 text-primary-light">✓</span>
              {feature}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  const pricingBlock = showPackages && (
    <div className="mb-12">
      {beforePackages}
      <div className={`grid grid-cols-1 ${
        service.packages.length === 2 ? 'md:grid-cols-2 max-w-4xl' : 'md:grid-cols-3'
      } gap-8 mx-auto`}>
        {service.packages.map((pkg, pIndex) => (
          <div key={pIndex} className={`service-card rounded-2xl overflow-hidden flex flex-col relative ${pkg.popular ? 'ring-4 ring-accent-500' : ''}`}>
            {pkg.popular && (
              <div className="absolute top-0 right-0 bg-accent-500 text-white text-sm font-semibold px-4 py-1 rounded-bl-lg z-10">
                Cel mai popular
              </div>
            )}
            <div className="bg-primary p-6 text-white">
              <h3 className="text-2xl font-bold text-center">{pkg.name}</h3>
              <p className="text-3xl font-bold text-center mt-4">
                {pkg.price} <span className="text-base font-normal opacity-80">MDL/lună</span>
              </p>
            </div>
            <div className="p-6 flex-grow flex flex-col">
              <ul className="space-y-4 flex-grow">
                {pkg.features.map((feature, fIndex) => (
                  <li key={fIndex} className="flex items-center text-gray-700">
                    <span className="mr-2 text-primary-light">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
              <button className="w-full bg-primary hover:bg-primary-dark text-white py-3 rounded-lg transition duration-300 transform hover:scale-105 mt-6">
                Comandă Acum
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section
      id={id}
      className={`py-20 px-4 scroll-mt-24 ${index % 2 === 0 ? 'bg-white' : 'metallic-gradient'}`}
      style={hasSpecialBackground ? sectionStyle : {}}
    >
      {hasSpecialBackground && (
        <div className="absolute inset-0 bg-black/50" style={{ zIndex: 1 }}></div>
      )}
      <div className="container mx-auto relative" style={{ zIndex: 2 }}>
        {showTitleBlock && (
          <div className="flex items-center justify-center mb-12">
            <div className="bg-primary p-4 rounded-2xl shadow-lg">
              {React.cloneElement(service.icon, { className: "h-12 w-12 text-white" })}
            </div>
            <h2 className={`text-3xl font-bold ml-4 ${hasSpecialBackground ? 'text-white' : 'text-primary'}`}>
              {service.title}
            </h2>
          </div>
        )}

        {pricingFirst ? (
          <>
            {pricingBlock}
            {descriptionBlock}
          </>
        ) : (
          <>
            {descriptionBlock}
            {pricingBlock}
          </>
        )}
      </div>
    </section>
  );
};

export default ServiceSection;